<template>
  <ChartCard data-widget-id="gender-distribution">
    <ChartHeader
      label="What is the gender distribution of our candidate pool?"
      widget-download-id="gender-distribution"
      tooltip-key="demo-survey-insights-bar"
    />
    <div class="chart-container">
      <BarChart id="dsr-gender-distribution" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </div>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed, ref } from 'vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { useColors } from '@/composables/colors';
import { initTooltip } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/tooltip';
import { flatten } from 'lodash-es';
import { numberWithCommas } from '@/helpers/formatters';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { datalabelsConfig } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/datalabelsConfig';

const { genderBreakdown } = storeToRefs(useDemographicSurveyReport());
const {
  male,
  female,
  declined,
  unknown,
} = genderBreakdown.value;

const {
  candidateNotSubmitted,
  candidateDeclined,
  candidateMale,
  candidateFemale,
  textDarkest,
  white,
} = useColors();

const overallTotal = computed(() => female + male + declined + unknown);

const femaleVal = computed(() => ((female || 0) / overallTotal.value) * 100);
const maleVal = computed(() => ((male || 0) / overallTotal.value) * 100);
const declinedVal = computed(() => ((declined || 0) / overallTotal.value) * 100);
const unknownVal = computed(() => ((unknown || 0) / overallTotal.value) * 100);

const sortArray = computed(() => [
  { key: 'female', value: femaleVal.value },
  { key: 'male', value: maleVal.value },
  { key: 'undefined', value: declinedVal.value + unknownVal.value },
].filter((item) => item.value > 0).sort((a, b) => b.value - a.value));

const plugins = ref([
  ChartDataLabels,
]);

const chartData = computed(() => {
  const datasets = [
    {
      label: 'Female',
      backgroundColor: candidateFemale.value,
      borderWidth: 0,
      borderRadius: 4,
      value: femaleVal.value,
      count: female,
      sortKey: 'female',
      maxBarThickness: 95,
    },
    {
      label: 'Male',
      backgroundColor: candidateMale.value,
      borderWidth: 0,
      borderRadius: 4,
      value: maleVal.value,
      count: male,
      sortKey: 'male',
      maxBarThickness: 95,
    },
    {
      label: 'Declined',
      backgroundColor: candidateDeclined.value,
      borderWidth: 0,
      borderRadius: 4,
      value: declinedVal.value,
      count: declined,
      sortKey: 'undefined',
      maxBarThickness: 95,
    },
    {
      label: 'Not Submitted',
      backgroundColor: candidateNotSubmitted.value,
      borderWidth: 0,
      borderRadius: 4,
      value: unknownVal.value,
      count: unknown,
      sortKey: 'undefined',
      maxBarThickness: 95,
    },
  ].filter((dataset) => dataset.value > 0).sort((a, b) => {
    const aIndex = sortArray.value.findIndex((item) => item.key === a.sortKey);
    const bIndex = sortArray.value.findIndex((item) => item.key === b.sortKey);
    return aIndex - bIndex;
  });

  datasets.forEach((dataset) => {
    const sortIndex = sortArray.value.findIndex((item) => item.key === dataset.sortKey);
    dataset.data = Array(sortArray.value.length).fill(0); // eslint-disable-line no-param-reassign
    dataset.data[sortIndex] = dataset.value; // eslint-disable-line no-param-reassign
  });

  const labels = datasets.map((dataset) => {
    if (dataset.label === 'Not Submitted') return 'Undefined';
    if (dataset.label === 'Declined') return '';
    return dataset.label;
  }).filter(Boolean);

  return {
    labels,
    datasets,
  };
});

const suggestedMax = computed(() => {
  const max = Math.max(femaleVal.value, maleVal.value, declinedVal.value + unknownVal.value);

  return Math.ceil(max / 10) * 10;
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        callback: (value) => `${value}%`,
        color: candidateNotSubmitted.value,
        font: {
          size: 10,
        },
        maxTicksLimit: suggestedMax.value / 10 + 1,
      },
    },
    x: {
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 13,
          weight: 500,
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    datalabels: datalabelsConfig(textDarkest, white, suggestedMax, '13px'),
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: (context) => initTooltip({
        context,
        tooltipId: 'gender-distribution-tooltip',
        createLineItems: (tooltip) => flatten(tooltip.dataPoints.map((point) => [
          { value: `Percentage: ${point.dataset.value.toFixed(1)}%` },
          { value: `Count: ${numberWithCommas(point.dataset.count || 0)}` },
        ])),
        createTitle: (tooltip) => tooltip.dataPoints[0].dataset.label,
      }),
    },
  },
}));
</script>

<style lang="less" scoped>
.chart-container {
  padding: 10px 16px 5px 10px;
  height: 320px;
  width: 100%;
}
</style>
